import React, { useMemo } from 'react';
import { Alert, Empty, Spin, Typography } from 'antd';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useWeatherPowerIndex, type Confidence } from '@/api/weatherPowerIndex';

const { Text } = Typography;

const confidenceColor: Record<Confidence, string> = {
  high: '#237804',
  medium: '#ad6800',
  low: '#a8071a',
};

function fmt(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toFixed(digits);
}

function metricBox(label: string, value: string, sub?: string, color = '#1f2937') {
  return (
    <div
      style={{
        minWidth: 150,
        padding: '12px 14px',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, lineHeight: 1.1, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{sub}</div>}
    </div>
  );
}

const WeatherPowerIndexPanel: React.FC = () => {
  const { data, isLoading, error } = useWeatherPowerIndex();

  const chartOption: EChartsOption = useMemo(() => {
    const hours = data?.hours ?? [];
    return {
      grid: { top: 24, right: 20, bottom: 28, left: 36 },
      tooltip: { trigger: 'axis' },
      legend: { top: 0, right: 0, itemWidth: 14, itemHeight: 8 },
      xAxis: {
        type: 'category',
        data: hours.map((h) => `${h.h}`),
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: { formatter: '{value}' },
        splitLine: { lineStyle: { color: '#eef2f7' } },
      },
      series: [
        {
          name: '光伏',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: hours.map((h) => h.solar_index),
          lineStyle: { color: '#f97316', width: 2 },
          itemStyle: { color: '#f97316' },
        },
        {
          name: '风电',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: hours.map((h) => h.wind_index),
          lineStyle: { color: '#0f766e', width: 2 },
          itemStyle: { color: '#0f766e' },
        },
        {
          name: '综合校正',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: hours.map((h) => h.corrected_combined_index),
          lineStyle: { color: '#2563eb', width: 3 },
          itemStyle: { color: '#2563eb' },
        },
      ],
    };
  }, [data]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginBottom: 12 }}>
        <Spin tip="加载新能源出力指数..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="新能源出力指数加载失败"
        description="气象地图仍可使用，指数摘要暂时不可用。"
        style={{ marginBottom: 12 }}
      />
    );
  }

  if (!data?.summary) {
    return (
      <div style={{ padding: 18, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginBottom: 12 }}>
        <Empty description={data?.message ?? '暂无新能源出力指数'} />
      </div>
    );
  }

  const { summary } = data;
  const topPrefectures = data.prefectures.slice(0, 8);

  return (
    <section
      style={{
        border: '1px solid #d9e2ec',
        borderRadius: 8,
        background: '#f8fafc',
        padding: 16,
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260, flex: '1 1 280px' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
            新能源出力指数
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            D+1 {data.date ?? ''} · 地市装机加权 · 指数范围 0-100
          </Text>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
            {summary.price_signal}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {metricBox('中午光伏指数', fmt(summary.solar_index), '10-14时', '#f97316')}
          {metricBox('全天风电指数', fmt(summary.wind_index), '24小时均值', '#0f766e')}
          {metricBox('综合校正指数', fmt(summary.corrected_combined_index), `原始 ${fmt(summary.combined_index)}`, '#2563eb')}
          {metricBox('可信度', summary.confidence_label, 'ensemble + 历史偏差', confidenceColor[summary.confidence])}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 12, minHeight: 240 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            24小时指数曲线
          </div>
          <ReactECharts option={chartOption} style={{ height: 210 }} opts={{ renderer: 'canvas' }} notMerge />
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            地市贡献 Top 8
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {topPrefectures.map((p, idx) => (
              <div key={p.prefecture} style={{ display: 'grid', gridTemplateColumns: '26px 54px 1fr 54px', alignItems: 'center', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{idx + 1}</Text>
                <Text strong>{p.name}</Text>
                <div style={{ height: 8, background: '#eef2f7', borderRadius: 4, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min(100, p.contribution_share * 100 * 4)}%`,
                      height: '100%',
                      background: '#2563eb',
                    }}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: 12, textAlign: 'right' }}>
                  {(p.contribution_share * 100).toFixed(1)}%
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
        {data.time_blocks.map((block) => (
          <div key={block.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Text strong>{block.label}</Text>
              <Text style={{ color: confidenceColor[block.confidence], fontWeight: 600 }}>
                可信度 {block.confidence === 'high' ? '高' : block.confidence === 'low' ? '低' : '中'}
              </Text>
            </div>
            <div style={{ marginTop: 8, fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
              {fmt(block.corrected_combined_index)}
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>校正指数</span>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>{block.note}</Text>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          主要贡献：{summary.main_drivers.join('、') || '-'}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          光伏校正因子：{summary.solar_correction_factor.toFixed(3)}
        </Text>
        {summary.risk_notes.map((note) => (
          <Text key={note} type="secondary" style={{ fontSize: 12 }}>
            {note}
          </Text>
        ))}
      </div>
    </section>
  );
};

export default WeatherPowerIndexPanel;
